/**
 * taalDetectie.js: welke taal moet de voorlezer gebruiken? (dyslexie, 09-10-2026)
 *
 * Twee lagen:
 *   1. taalVanVak(vak): het vak bepaalt de standaardtaal ("Engels (tto)" -> en),
 *      via het vakkenregister.
 *   2. detecteerTaal(zin, standaardTaal): per zin een stopwoorden-telling, zodat
 *      een Nederlandse vraag bij een Engelse leestekst toch Nederlands klinkt.
 *      Eén taal met treffers wint, ook met maar één treffer ("Noem twee
 *      redenen." in een Engelse toets is Nederlands). Bij gelijkspel of zonder
 *      treffers wint de standaardtaal.
 */

import { vakProfiel } from '../config/vakken';

export const TALEN = { nl: 'Nederlands', en: 'Engels', fr: 'Frans', de: 'Duits', es: 'Spaans' };

const GELDIG = new Set(Object.keys(TALEN));

/**
 * Vaknaam -> taalcode, uit het vakkenregister (src/config/vakken.js). "Engels (tto)"
 * en "entl" tellen als Engels; "Engelstalige economie" is economie, dus Nederlands.
 * Een onbekend vak is Nederlands.
 */
export function taalVanVak(vak) {
  const taal = vakProfiel(vak)?.taal ?? 'nl';
  return GELDIG.has(taal) ? taal : 'nl';
}

// Stopwoorden per taal: lidwoorden, voornaamwoorden, voorzetsels, hulpwerk-
// woorden en voegwoorden. Overlap tussen talen (de, en, in, is ...) mag: de
// woorden die maar in één taal voorkomen geven de doorslag.
const STOPWOORDEN = {
  nl: [
    'de', 'het', 'een', 'en', 'van', 'in', 'is', 'dat', 'op', 'te', 'zijn', 'voor', 'met', 'niet',
    'aan', 'er', 'ook', 'als', 'maar', 'om', 'bij', 'dan', 'nog', 'wat', 'uit', 'naar', 'dit',
    'deze', 'die', 'hij', 'zij', 'ze', 'wij', 'we', 'jij', 'je', 'ik', 'u', 'mij', 'hem', 'haar',
    'hun', 'ons', 'onze', 'mijn', 'jouw', 'zich', 'wordt', 'worden', 'werd', 'werden', 'heeft',
    'hebben', 'had', 'hadden', 'kan', 'kunnen', 'kon', 'zal', 'zullen', 'zou', 'zouden', 'moet',
    'moeten', 'mag', 'mogen', 'wil', 'willen', 'was', 'waren', 'geen', 'wel', 'hoe', 'waarom',
    'welke', 'welk', 'hier', 'daar', 'over', 'onder', 'door', 'tussen', 'tegen', 'zonder',
    'omdat', 'want', 'dus', 'toen', 'nu', 'al', 'altijd', 'nooit', 'veel', 'meer', 'minder',
    'elke', 'alle', 'iets', 'niets', 'leg', 'noem', 'bereken', 'waar',
    // Opdrachtwoorden in vraagteksten (korte zinnen halen anders geen treffer).
    'kies', 'schrijf', 'geef', 'lees', 'vul', 'zet', 'maak', 'beschrijf', 'verklaar', 'vertaal',
    'juiste', 'antwoord', 'vraag', 'tekst', 'woord', 'woorden', 'zin', 'zinnen', 'twee', 'drie',
    'volgende', 'onderstaande', 'bovenstaande', 'redenen', 'voorbeeld', 'samenvatting',
  ],
  en: [
    'the', 'a', 'an', 'and', 'of', 'in', 'is', 'that', 'to', 'for', 'with', 'not', 'on', 'at',
    'by', 'from', 'as', 'but', 'or', 'it', 'this', 'these', 'those', 'he', 'she', 'they', 'we',
    'you', 'i', 'me', 'him', 'her', 'them', 'us', 'my', 'your', 'his', 'their', 'our', 'its',
    'be', 'been', 'being', 'are', 'was', 'were', 'has', 'have', 'had', 'do', 'does', 'did',
    'can', 'could', 'will', 'would', 'shall', 'should', 'may', 'might', 'must', 'what', 'which',
    'who', 'whom', 'whose', 'where', 'when', 'why', 'how', 'there', 'here', 'into', 'about',
    'over', 'under', 'between', 'through', 'after', 'before', 'because', 'so', 'if', 'then',
    'than', 'very', 'more', 'most', 'some', 'any', 'all', 'no', 'yes', 'also', 'only', 'just',
    'each', 'every', 'both', 'other', 'another', 'explain', 'describe',
    'choose', 'write', 'read', 'fill', 'give', 'name', 'translate', 'answer', 'question',
    'text', 'word', 'words', 'sentence', 'sentences', 'following', 'correct', 'reasons', 'example',
  ],
  fr: [
    'le', 'la', 'les', 'un', 'une', 'des', 'du', 'de', 'et', 'en', 'est', 'que', 'qui', 'dans',
    'pour', 'pas', 'ne', 'sur', 'avec', 'ce', 'cette', 'ces', 'cet', 'il', 'elle', 'ils',
    'elles', 'nous', 'vous', 'je', 'tu', 'on', 'me', 'te', 'se', 'lui', 'leur', 'leurs', 'mon',
    'ma', 'mes', 'ton', 'ta', 'tes', 'son', 'sa', 'ses', 'notre', 'nos', 'votre', 'vos', 'au',
    'aux', 'à', 'y', 'ou', 'où', 'mais', 'donc', 'car', 'si', 'comme', 'plus', 'moins', 'très',
    'aussi', 'tout', 'tous', 'toute', 'toutes', 'être', 'avoir', 'été', 'sont', 'ont', 'fait',
    'faire', 'peut', 'peuvent', 'doit', 'doivent', 'était', 'étaient', 'sera', 'seront', 'suis',
    'es', 'sommes', 'êtes', 'ai', 'avons', 'avez', 'quel', 'quelle', 'quels', 'quelles', 'quoi',
    'pourquoi', 'comment', 'quand', 'chez', 'sans', 'sous', 'entre', 'vers', 'depuis', 'pendant',
    'aucun', 'aucune', 'chaque', 'quelque', 'quelques', 'expliquez', 'décrivez',
    'choisissez', 'choisis', 'écrivez', 'écris', 'lisez', 'lis', 'complétez', 'traduisez', 'réponse',
    'question', 'texte', 'mot', 'mots', 'phrase', 'phrases', 'suivant', 'suivante', 'correcte', 'exemple',
  ],
  de: [
    'der', 'die', 'das', 'ein', 'eine', 'einen', 'einem', 'einer', 'eines', 'und', 'ist', 'sind',
    'nicht', 'zu', 'mit', 'auf', 'für', 'von', 'dem', 'den', 'des', 'im', 'am', 'ins', 'ans',
    'bei', 'nach', 'aus', 'vor', 'über', 'unter', 'zwischen', 'durch', 'gegen', 'ohne', 'um',
    'als', 'auch', 'aber', 'oder', 'wenn', 'dass', 'weil', 'denn', 'noch', 'nur', 'schon',
    'sehr', 'ich', 'du', 'er', 'sie', 'es', 'wir', 'ihr', 'mich', 'dich', 'ihn', 'uns', 'euch',
    'ihnen', 'mein', 'dein', 'sein', 'seine', 'ihre', 'unser', 'euer', 'sich', 'war', 'waren',
    'wird', 'werden', 'wurde', 'wurden', 'hat', 'haben', 'hatte', 'hatten', 'kann', 'können',
    'konnte', 'muss', 'müssen', 'soll', 'sollen', 'will', 'wollen', 'darf', 'dürfen', 'möchte',
    'was', 'wer', 'wie', 'wo', 'warum', 'welche', 'welcher', 'welches', 'wann', 'hier', 'dort',
    'dieser', 'diese', 'dieses', 'jeder', 'jede', 'jedes', 'alle', 'kein', 'keine', 'viel',
    'mehr', 'man', 'erkläre', 'beschreibe', 'nenne',
    'wähle', 'wählen', 'schreibe', 'schreiben', 'lies', 'lesen', 'übersetze', 'antwort', 'frage',
    'text', 'wort', 'wörter', 'satz', 'sätze', 'folgende', 'folgenden', 'richtige', 'richtig', 'beispiel',
  ],
  es: [
    'el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas', 'y', 'o', 'de', 'del', 'en', 'es',
    'son', 'que', 'qué', 'por', 'para', 'con', 'sin', 'no', 'sí', 'se', 'su', 'sus', 'mi', 'mis',
    'tu', 'tus', 'nuestro', 'nuestra', 'yo', 'tú', 'él', 'ella', 'ellos', 'ellas', 'nosotros',
    'vosotros', 'usted', 'ustedes', 'me', 'te', 'le', 'les', 'lo', 'nos', 'os', 'al', 'a', 'como',
    'cómo', 'pero', 'porque', 'cuando', 'cuándo', 'donde', 'dónde', 'quién', 'quiénes', 'cuál',
    'cuáles', 'muy', 'más', 'menos', 'también', 'todo', 'todos', 'toda', 'todas', 'este', 'esta',
    'esto', 'estos', 'estas', 'ese', 'esa', 'eso', 'aquel', 'ser', 'estar', 'está', 'están',
    'era', 'eran', 'fue', 'fueron', 'ha', 'han', 'hay', 'había', 'tiene', 'tienen', 'puede',
    'pueden', 'debe', 'deben', 'hacer', 'hace', 'desde', 'hasta', 'entre', 'sobre', 'contra',
    'cada', 'algo', 'nada', 'nadie', 'alguien', 'siempre', 'nunca', 'ya', 'aquí', 'allí',
    'explica', 'describe',
    'elige', 'escribe', 'lee', 'completa', 'traduce', 'respuesta', 'pregunta', 'texto', 'palabra',
    'palabras', 'frase', 'frases', 'siguiente', 'correcta', 'correcto', 'ejemplo', 'razones',
  ],
};

const SETS = Object.fromEntries(Object.entries(STOPWOORDEN).map(([k, v]) => [k, new Set(v)]));

// Formules tellen niet mee (zelfde regel als formuleTekst.js).
// Zonder regex-lookbehind (Safari/iPadOS < 16.4), zie geenLookbehind.test.js.
export const FORMULE_REGEX = /\$\$[\s\S]+?\$\$|\$(?=\S)(?:\\.|[^$\\])*?(?:\\\S|[^$\\\s])\$/g;

/** Woorden uit een zin: alleen letters, kleine letters; getallen en formules vallen af. */
export function woordenVan(zin) {
  const schoon = String(zin == null ? '' : zin).replace(FORMULE_REGEX, ' ').toLowerCase();
  const woorden = schoon.match(/\p{L}+(?:['’]\p{L}+)?/gu) || [];
  // "l'école" -> "l" en "école"; "don't" blijft bruikbaar via het deel voor de apostrof.
  return woorden.flatMap((w) => w.split(/['’]/)).filter(Boolean);
}

/**
 * Detecteert de taal van één zin op stopwoorden.
 *
 * Regel: de taal met de meeste treffers wint. Eén treffer is genoeg als alle
 * andere talen nul hebben (korte opdrachtzinnen als "Noem twee redenen."
 * halen anders nooit de drempel en zouden in een Engelse toets Engels
 * klinken). Bij gelijkspel of zonder treffers wint de standaardtaal.
 *
 * @param {string} zin
 * @param {string} standaardTaal 'nl' | 'en' | 'fr' | 'de' | 'es' (bij twijfel)
 */
export function detecteerTaal(zin, standaardTaal = 'nl') {
  const standaard = GELDIG.has(standaardTaal) ? standaardTaal : 'nl';
  const woorden = woordenVan(zin);
  if (woorden.length === 0) return standaard;
  const telling = {};
  for (const taal of Object.keys(SETS)) telling[taal] = 0;
  for (const w of woorden) {
    for (const taal of Object.keys(SETS)) if (SETS[taal].has(w)) telling[taal] += 1;
  }
  let beste = null;
  let hoogste = 0;
  let tweede = 0;
  for (const [taal, n] of Object.entries(telling)) {
    if (n > hoogste) { tweede = hoogste; hoogste = n; beste = taal; } else if (n > tweede) tweede = n;
  }
  if (!beste || hoogste === 0 || hoogste === tweede) return standaard;
  return beste;
}
